import React, { memo, useCallback, useRef } from 'react'
import {
  Controller,
  ControllerProps,
  FieldErrors,
  FieldValues,
  get,
  Path,
  PathValue,
} from 'react-hook-form'
import { GroupBase } from 'react-select'

import {
  Select,
  SelectBaseOption,
  SelectProps,
  StyleProps,
} from '@surglogs-app/ui'

import { DefaultFormField, DefaultFormFieldProps } from './DefaultFormField'

interface SelectFieldProps<
  TValuesModel extends FieldValues,
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends SelectProps<Option, IsMulti, Group>,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name'
    > {
  name: Path<TValuesModel>
  defaultValue?: PathValue<TValuesModel, Path<TValuesModel>>
  errors?: FieldErrors<any>
  warnings?: Record<string, any>
  formFieldProps?: Omit<
    DefaultFormFieldProps,
    'name' | 'errors' | 'warnings' | 'children'
  > &
    StyleProps
}

const Component = <
  TValuesModel extends FieldValues,
  Option extends SelectBaseOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  control,
  defaultValue,
  errors,
  warnings,
  name,
  invalid,
  shouldUnregister,
  warning,
  onChange: handleChange,
  ...rest
}: SelectFieldProps<TValuesModel, Option, IsMulti, Group>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const onFocus = useCallback(() => inputRef.current?.focus(), [])

  return (
    <Controller<TValuesModel, Path<TValuesModel>>
      {...{
        defaultValue,
        control,
        name,
        onFocus,
        shouldUnregister,
        render: ({ field: { onBlur, onChange, value } }) => (
          <Select<Option, IsMulti, Group>
            {...rest}
            {...{
              onBlur,
              onChange: handleChange
                ? (...args) => {
                    handleChange?.(...args)
                    return onChange(...args)
                  }
                : onChange,
              invalid: invalid ?? get(errors || {}, name),
              warning: warning ?? get(warnings || {}, name),
              ref: inputRef,
              value,
            }}
          />
        ),
      }}
    />
  )
}

export const SelectField = memo(
  <
    TValuesModel extends FieldValues,
    Option extends SelectBaseOption,
    IsMulti extends boolean,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >({
    formFieldProps = undefined,
    ...rest
  }: SelectFieldProps<TValuesModel, Option, IsMulti, Group>) => {
    if (formFieldProps) {
      return (
        <DefaultFormField
          {...{
            ...formFieldProps,
            name: rest.name,
            errors: rest.errors,
            warnings: rest.warnings,
            invalid: rest.invalid,
          }}>
          <Component {...rest} />
        </DefaultFormField>
      )
    }

    return <Component {...rest} />
  },
) as typeof Component
